import useSuitesProjectsLanguage from '../useSuitesProjectsLanguage';
import React from 'react';
import { X, Settings2, Monitor, Tablet, Globe } from 'lucide-react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';

export default function SuiteConfigDialog({
  showConfigModal,
  selectedSuite,
  setShowConfigModal,
  flowConfig,
  setFlowConfig,
  availableEnvironments,
  handleUpdateConfig
}) {
  const { t } = useSuitesProjectsLanguage();
  return (<>
      {/* Configuration Modal */}
      {showConfigModal && selectedSuite && (
        <div className="lexa-suite-overlay fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <Card className="lexa-suite-dialog lexa-suite-dialog--config w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <Settings2 className="w-5 h-5 mr-2 text-indigo-600" />
                <h2 className="text-lg font-semibold">{t("Execution Configuration")}</h2>
              </div>
              <Button variant="ghost" size="sm" aria-label={t("Close")} onClick={() => setShowConfigModal(false)}>
                <X className="w-5 h-5" />
              </Button>
            </div>

            <p className="text-sm text-gray-500 mb-4">
              {t("Configure how flow tests in this suite will be executed.")}
            </p>

            <div className="space-y-4">
              <div className="lexa-suite-config-grid grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t("Browser")}</label>
                  <select
                    className="w-full px-3 py-2 border rounded-lg text-sm bg-white"
                    value={flowConfig.browser}
                    onChange={(e) => setFlowConfig({ ...flowConfig, browser: e.target.value })}
                  >
                    <option value="chromium">Chrome</option>
                    <option value="firefox">Firefox</option>
                    <option value="webkit">Safari</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t("Device Type")}</label>
                  <select
                    className="w-full px-3 py-2 border rounded-lg text-sm bg-white"
                    value={flowConfig.device_type}
                    onChange={(e) => setFlowConfig({ ...flowConfig, device_type: e.target.value })}
                  >
                    <option value="desktop">{t("Desktop")}</option>
                    <option value="mobile">{t("Mobile")}</option>
                    <option value="tablet">{t("Tablet")}</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t("Environment")}</label>
                <select
                  className="w-full px-3 py-2 border rounded-lg text-sm bg-white"
                  value={flowConfig.environment_id || ''}
                  onChange={(e) => setFlowConfig({ ...flowConfig, environment_id: e.target.value || null })}
                >
                  <option value="">{t("Use test's default environment")}</option>
                  {availableEnvironments.map(env => (
                    <option key={env.id} value={env.id}>{env.name} {env.base_url ? `(${env.base_url})` : ''}</option>
                  ))}
                </select>
              </div>

              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center">
                  <Monitor className="w-4 h-4 mr-2 text-gray-500" />
                  <span className="text-sm text-gray-700">{t("Headless Mode")}</span>
                </div>
                <div className="flex items-center">
                  <span className="text-xs bg-indigo-100 text-indigo-700 px-2 py-1 rounded mr-2">{t("Always On")}</span>
                  <input
                    type="checkbox"
                    checked={true}
                    disabled
                    className="rounded opacity-50 cursor-not-allowed"
                  />
                </div>
              </div>

              <div className="p-3 bg-blue-50 rounded-lg">
                <div className="flex items-start">
                  <Globe className="w-4 h-4 mr-2 text-blue-500 mt-0.5" />
                  <div className="text-xs text-blue-700">
                    <p className="font-medium mb-1">{t("Current Configuration")}</p>
                    <p>{t("Browser:")} {flowConfig.browser} {t("• Device:")} {t(flowConfig.device_type)}</p>
                    <p>{t("Environment:")} {flowConfig.environment_id
                      ? availableEnvironments.find(e => e.id === flowConfig.environment_id)?.name || t("Selected")
                      : t("Test default")
                    }</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <Button variant="outline" onClick={() => setShowConfigModal(false)}>
                {t("Cancel")}
              </Button>
              <Button className="lexa-suite-primary-action" onClick={handleUpdateConfig}>
                {t("Save Configuration")}
              </Button>
            </div>
          </Card>
        </div>
      )}
  </>);
}
